import { useQueryClient } from "@tanstack/react-query";
import { RefreshCwIcon } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  cancelPendingReloads,
  dismissBuildRefresh,
  markChunkError,
  onRouteChange,
  reloadNow,
  useBuildRefresh,
  watchQueryClient,
} from "./build-refresh.ts";

export const BUILD_REFRESH_TOAST_ID = "esi-build-refresh";

// The app's small ink and ghost buttons, over sonner's own button styles.
const BUTTON =
  "h-7! rounded-md! px-2.5! text-[13px]! font-medium! focus-visible:ring-2! focus-visible:ring-ring! focus-visible:ring-offset-1! focus-visible:ring-offset-popover! focus-visible:shadow-none!";
const INK = "bg-action! text-action-on! hover:bg-action-hover!";
const GHOST = "bg-transparent! text-fg-secondary! hover:bg-accent! hover:text-accent-foreground!";

const NARROW = "(max-width: 639px)";
const subscribeNarrow = (listener: () => void) => {
  if (typeof window.matchMedia !== "function") {
    return () => {};
  }
  const query = window.matchMedia(NARROW);
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
};
const isNarrow = () => typeof window.matchMedia === "function" && window.matchMedia(NARROW).matches;

/**
 * A new build is waiting (build-refresh.ts): one quiet, persistent toast offers the reload until
 * a safe point takes it, the person dismisses it for that build, or presses Reload (which goes
 * past every hold). Also listens for the other safe point (back and forward) and for lazy
 * chunks a deploy replaced, and holds automatic reloads while queries or mutations are in flight.
 */
export function BuildRefresh() {
  const { t } = useTranslation();
  const { target, required, toast: due } = useBuildRefresh();
  const narrow = useSyncExternalStore(subscribeNarrow, isNarrow);
  const queryClient = useQueryClient();

  useEffect(() => watchQueryClient(queryClient), [queryClient]);

  useEffect(() => {
    const onPopState = () => {
      onRouteChange();
    };
    const onPreloadError = () => {
      // The import still fails as before (no preventDefault): its caller shows its error state.
      void markChunkError();
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("vite:preloadError", onPreloadError);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("vite:preloadError", onPreloadError);
      // No reload attempt outlives the app: its timers and settle waits end here.
      cancelPendingReloads();
    };
  }, []);

  useEffect(() => {
    if (!due || target === null) {
      toast.dismiss(BUILD_REFRESH_TOAST_ID);
      return;
    }
    // Also called when the code closes it (no longer due): then it changes nothing.
    const dismiss = () => dismissBuildRefresh(target);
    toast(t("buildRefresh.available"), {
      id: BUILD_REFRESH_TOAST_ID,
      description: required ? t("buildRefresh.required") : undefined,
      icon: <RefreshCwIcon className="size-4" aria-hidden />,
      duration: Number.POSITIVE_INFINITY,
      action: { label: t("buildRefresh.reload"), onClick: reloadNow },
      cancel: { label: t("buildRefresh.later"), onClick: dismiss },
      onDismiss: dismiss,
      // On a phone the corner toast would sit on the composer, over the draft it waits for.
      position: narrow ? "top-center" : undefined,
      classNames: { actionButton: `${BUTTON} ${INK}`, cancelButton: `${BUTTON} ${GHOST}` },
    });
  }, [due, target, required, narrow, t]);

  return null;
}
